// TODO: Update import from core index (can't read default from undefined)
// import { ShipmentHeaders } from 'configurations'
import { ShipmentHeaders } from 'configurations/ShipmentEntity/ShipmentHeaders';
import deepEqual from 'fast-deep-equal';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ALL_FILTERS } from 'utils/constants/Filters';
import { FilterContext, ListContext } from 'utils/context';
import { TFilter, TFilterValue } from 'utils/context/FilterContext/types';
import { useSavedOrders, useSavedShipments } from 'utils/hooks';
import { ISavedViewsHook } from 'utils/hooks/useSavedViews/useSavedViews';

import { SavedView as TSavedView } from '@project44-models/shipment-list';

import { TViewContext, TViewType } from './types';

const defaultSelected = (name: TFilterValue): TSavedView => {
  const isOrder = name === 'all-orders';
  const listView = isOrder ? 'all-orders' : 'all-shipments';
  const viewType = isOrder ? 'order' : 'shipment';
  return {
    id: listView,
    name: isOrder ? 'All orders' : 'All shipments',
    filterCriteria: { listView, viewType },
    displayOptions: { activeColumns: [] },
  };
};

const defaultSavedView: ISavedViewsHook = {
  data: { views: [] },
  isError: false,
  isLoading: false,
  createView: () => new Promise(() => ({})),
  deleteView: () => new Promise(() => ({})),
  sortViews: () => new Promise(() => ({})),
  updateView: () => new Promise(() => ({})),
};

const ViewContext = createContext<TViewContext>({
  canUpdate: false,
  getViewRequest: () => ({}),
  revertView: () => false,
  savedOrders: defaultSavedView,
  savedShipments: defaultSavedView,
  selectedView: defaultSelected('all-shipments'),
  setAllDisplayOptions: () => null,
  setWillSearch: () => null,
  viewType: 'shipment' as TViewType,
});

interface IViewContextProvider {
  children: React.ReactNode;
}

function ViewContextProvider({ children }: IViewContextProvider) {
  const {
    filters,
    hasFilters,
    getOrdersRequest,
    getShipmentsRequest,
    paramsFromOrder,
    paramsFromShipment,
    setFilters,
  } = useContext(FilterContext);

  const { doSearchOrders, doSearchShipments, doCountOrders, doCountShipments } =
    useContext(ListContext);

  const savedShipments = useSavedShipments();
  const savedOrders = useSavedOrders();
  const isLoading = savedShipments.isLoading || savedOrders.isLoading;

  const [didMount, setDidMount] = useState(false);
  const [willSearch, setWillSearch] = useState(false);
  const [viewId, setViewId] = useState(filters.listView);
  const [allDisplayOptions, setAllDisplayOptions] = useState({
    activeColumns: ShipmentHeaders,
  });

  const [view, viewType, viewParams] = useMemo(() => {
    const findView = (views: TSavedView[]) =>
      views.find(({ id }) => id === filters.listView);
    const order = findView(savedOrders.data.views);
    const shipment = findView(savedShipments.data.views);
    const nextView = order || shipment || defaultSelected(filters.listView);
    const isOrder = !!order || filters.listView === 'all-orders';
    const nextType = isOrder ? 'order' : 'shipment';
    const getParams = isOrder ? paramsFromOrder : paramsFromShipment;
    const viewParams = getParams(nextView.filterCriteria);
    return [nextView, nextType, viewParams] as [TSavedView, TViewType, TFilter];
  }, [
    filters.listView,
    paramsFromOrder,
    paramsFromShipment,
    savedOrders.data.views,
    savedShipments.data.views,
  ]);

  useEffect(() => {
    if (!isLoading) {
      if (!didMount || filters.listView !== viewId) {
        setDidMount(true);
        setViewId(view?.id || '');
        setWillSearch(true);
        setFilters(
          (prev) =>
            ({
              ...(!didMount && hasFilters(ALL_FILTERS) ? prev : viewParams),
              listView: view.id,
              viewType,
            }) as TFilter,
        );
      }
    }
  }, [
    didMount,
    filters.listView,
    hasFilters,
    isLoading,
    setFilters,
    viewId,
    view,
    viewType,
    viewParams,
  ]);

  useEffect(() => {
    if (willSearch) {
      setWillSearch(false);
      doCountOrders();
      doCountShipments();

      if (viewType === 'order') {
        doSearchOrders();
      } else {
        const active =
          (view.displayOptions?.activeColumns.length &&
            view.displayOptions?.activeColumns) ||
          allDisplayOptions.activeColumns;
        const added = active.filter(({ id }) => /ref-/i.test(id));
        const addedLabels = added.map(({ label }) => label);
        doSearchShipments(undefined, addedLabels);
      }
    }
  }, [
    allDisplayOptions,
    doCountOrders,
    doCountShipments,
    doSearchOrders,
    doSearchShipments,
    view.id,
    view.displayOptions,
    viewType,
    willSearch,
  ]);

  const values = useMemo(() => {
    const isOrder = viewType === 'order';
    return {
      canUpdate: !deepEqual(
        { listView: view.id, viewType, ...viewParams },
        filters,
      ),
      getViewRequest: isOrder ? getOrdersRequest : getShipmentsRequest,
      revertView: () => {
        setFilters({
          listView: view.id as unknown as TFilterValue,
          viewType,
          ...viewParams,
        });
      },
      savedOrders,
      savedShipments,
      selectedView: view,
      setAllDisplayOptions,
      setWillSearch: () => setWillSearch(true),
      viewType,
    };
  }, [
    filters,
    getOrdersRequest,
    getShipmentsRequest,
    savedOrders,
    savedShipments,
    setFilters,
    view,
    viewParams,
    viewType,
  ]);

  return <ViewContext.Provider value={values}>{children}</ViewContext.Provider>;
}

export { ViewContext, ViewContextProvider };
